import { useCallback, useEffect, useState } from 'react';
import { usePromise } from 'common/hooks/usePromise';
import { isITPSupported, shouldUseBreachBulkUpdateAPI } from 'common/utilities/FlightSetting';
import { pageDataService } from 'common/utilities/PageDataService';
import { ApiCallResultStatus } from 'store/apiCallResultStatus';
import { useAppDispatch, useAppSelector } from 'store/hooks';

import type { BreachEntity, BreachEntityState, CompositeBreachEntityType, SingleValueBreachEntityType } from '../breach.types';
import { extractNewOrRemediatedBreachEntities, getBreachById } from '../breachSelectors';
import {
  batchUpdateBreachEntityStateAsync,
  batchUpdateBreachStateAsync,
  getBreachByIdAsync,
  updateBreachEntityStateAsync,
} from '../identityMonitoringSlice';

import { useIdentityOnboardingStatus } from './useIdentityOnboardingStatus';

/**
 * Helper hook for getting breach state change functions
 *
 * @param breachId Id of a breach to load
 */
export const useBreach = (breachId: string) => {
  const dispatch = useAppDispatch();
  const scopePromise = usePromise();

  const isWebView = pageDataService.isWebView();
  const { csid } = useAppSelector((state) => state.identityMonitoring.userContext);
  const breach = useAppSelector((state) => getBreachById(state.identityMonitoring, breachId, csid));
  const { itmOnboarded } = useIdentityOnboardingStatus();
  const [getBreachByIdApiCallStatus, setGetBreachByIdApiCallStatus] = useState<ApiCallResultStatus>(ApiCallResultStatus.DEFAULT);

  // Fetch a breach only if breach is not found in the current store.
  // Note that since ITP landing page loads all breaches, breach should be found. In webview dialog scenarios,
  // breach won't be found since dialog doesn't load all breaches.
  // WebView perf optimization: load a breach w/o waiting for onboarding status (familyItpSummary API)
  const shouldLoadBreach = !breach && !!csid && isITPSupported() && (isWebView || itmOnboarded);
  useEffect(() => {
    if (shouldLoadBreach) {
      scopePromise(dispatch(getBreachByIdAsync({ breachId, csid })).unwrap())
        .then(() => {
          setGetBreachByIdApiCallStatus(ApiCallResultStatus.SUCCESS);
        })
        .catch(() => {
          setGetBreachByIdApiCallStatus(ApiCallResultStatus.FAILED);
        });
    }
  }, [dispatch, csid, shouldLoadBreach, breachId, scopePromise]);

  const updateCompositeEntityStates = useCallback(
    async (entities: BreachEntity<CompositeBreachEntityType>[], state: BreachEntityState) => {
      // eslint-disable-next-line no-restricted-syntax
      for (const e of entities) {
        // There is currently no API for bulk updating composite breach entities
        // eslint-disable-next-line no-await-in-loop
        await dispatch(
          updateBreachEntityStateAsync({
            breachId,
            entityId: e.entityId,
            breachedType: e.entityType,
            state,
            csid,
          })
        ).unwrap(); // unwrap to catch error using `onrejected` callback (see https://redux-toolkit.js.org/api/createAsyncThunk#checking-errors-after-dispatching)
      }
    },
    [dispatch, breachId, csid]
  );

  const updateSingleValueBreachEntityStates = useCallback(
    (entities: BreachEntity<SingleValueBreachEntityType>[], state: BreachEntityState) =>
      entities.length === 0
        ? undefined
        : dispatch(
            batchUpdateBreachEntityStateAsync({
              breachId,
              entityIds: entities.map((e) => e.entityId),
              state,
              csid,
            })
          ).unwrap(), // unwrap to catch error using `onrejected` callback (see https://redux-toolkit.js.org/api/createAsyncThunk#checking-errors-after-dispatching)
    [dispatch, breachId, csid]
  );

  const updateBreachEntityStates = useCallback(
    (entities: BreachEntity[], state: BreachEntityState) => {
      if (shouldUseBreachBulkUpdateAPI()) {
        return scopePromise(
          dispatch(
            batchUpdateBreachEntityStateAsync({
              breachId,
              entityIds: entities.map((e) => e.entityId),
              state,
              csid,
            })
          ).unwrap()
        );
      }

      const compositeEntities: BreachEntity<CompositeBreachEntityType>[] = [];
      const singleValueBreachEntities: BreachEntity<SingleValueBreachEntityType>[] = [];

      // Using `forEach` + `push` instead of `filter` for type-safety
      // (i.e. if CompositeBreachEntityType/SingleValueBreachEntityType changes, the following will fail to compile)
      entities.forEach((entity) => {
        if (entity.entityType === 'Address') {
          compositeEntities.push(entity);
        } else {
          singleValueBreachEntities.push(entity);
        }
      });

      // There is currently no API for bulk updating composite and single value entities together
      return scopePromise(
        // APIs must be called sequentially due to concurrency issue on breach state update on backend
        updateCompositeEntityStates(compositeEntities, state).then(() =>
          updateSingleValueBreachEntityStates(singleValueBreachEntities, state)
        )
      );
    },
    [breachId, csid, dispatch, scopePromise, updateCompositeEntityStates, updateSingleValueBreachEntityStates]
  );

  const completeBreach = useCallback(
    (complete = true) => {
      if (breach) {
        if (shouldUseBreachBulkUpdateAPI()) {
          return scopePromise(
            dispatch(
              batchUpdateBreachStateAsync({
                breachIds: [breach.breachId],
                state: complete ? 'Remediated' : 'New',
                csid,
              })
            ).unwrap()
          );
        }

        const entities = extractNewOrRemediatedBreachEntities(breach);
        return updateBreachEntityStates(entities, complete ? 'Remediated' : 'New');
      }
      return undefined;
    },
    [breach, csid, dispatch, scopePromise, updateBreachEntityStates]
  );

  return { getBreachByIdApiCallStatus, breach, updateBreachEntityStates, completeBreach };
};
